/**
 * COM-8.9 — reading the three ops health checks, and declaring the one setting
 * that switches the first of them on.
 *
 * ⚠️ THIS FILE HOLDS NO RULES. What each answer MEANS is
 * `commissionOpsHealth.ts`; the readers decide what is true.
 *
 * ⚠️ ALL THREE READERS ANSWER ZERO ROWS TO AN UNAUTHORISED CALLER WITHOUT
 * REFUSING. Measured 2026-08-26. So an empty result from any of them is not an
 * all-clear until `hasCommissionAuthority('commission.view', tenant)` has said
 * the caller was allowed to ask. This module cannot tell the difference and
 * deliberately does not try.
 *
 * ⚠️ ERRORS ARE THROWN, NOT SWALLOWED. A health card that renders a failed read
 * as three green ticks is worse than no card.
 */

import { supabase } from '@/lib/supabaseClient';
import { updateTenantSettings, clearTenantSettingsCache } from '@/services/tenantSettingsService';
import { tryDecimal } from '@/lib/money';
import type { ReversedStillPayable, StaleHold } from '@/lib/commissionOpsHealth';
import type { AccountReadinessCheck } from '@/lib/commissionAccountReadiness';
import {
  toAgreements,
  type AssignmentAgreement,
  type AssignmentAgreementRow,
} from '@/lib/commissionAgreements';

function fail(what: string, error: { message: string } | null): void {
  if (error) throw new Error(`${what}: ${error.message}`);
}

/**
 * Projects whose commission has been held longer than this account's limit.
 *
 * ⚠️ THIS RETURNS ZERO ROWS WHEN NO LIMIT IS DECLARED, AND THAT IS NOT HEALTH.
 * The function's own WHERE clause carries
 * `s.commission_payability_alert_days IS NOT NULL`, and that column is NULL for
 * every account. Callers MUST read the threshold separately and pass both to
 * `holdAlertState()`; treating this empty array as "nothing is stale" reports a
 * switched-off check as a clean bill of health.
 */
export async function fetchStaleHolds(): Promise<StaleHold[]> {
  const { data, error } = await supabase.rpc('commission_stale_payability_holds');
  fail('Could not read held lines', error);
  return ((data as Record<string, unknown>[] | null) ?? []).map((r) => ({
    projectId: (r.project_id as string) ?? null,
    shortCode: (r.short_code as string) ?? null,
    lines: (r.lines as number) ?? 0,
    heldAmount: tryDecimal(r.held_amount),
    daysHeld: (r.days_held as number) ?? null,
    thresholdDays: (r.threshold_days as number) ?? null,
    condition: (r.condition as string) ?? null,
  }));
}

/**
 * Lines that were reversed and are somehow still payable, which would pay twice.
 *
 * ⚠️ SECURITY INVOKER, unlike the other two (`prosecdef = false`, measured). RLS
 * bounds it to what the caller can see, so an empty result speaks for this
 * account and not for the product. `reversedScopeCaveat` is the sentence that
 * says so, and it is not optional.
 */
export async function fetchReversedStillPayable(): Promise<ReversedStillPayable[]> {
  const { data, error } = await supabase.rpc('commission_reversed_lines_still_payable');
  fail('Could not read reversed lines', error);
  return ((data as Record<string, unknown>[] | null) ?? []).map((r) => ({
    lineId: r.line_id as string,
    paymentState: (r.payment_state as string) ?? null,
    periodKey: (r.period_key as string) ?? null,
    amount: tryDecimal(r.amount),
    problem: (r.problem as string) ?? null,
  }));
}

/**
 * Every standing assignment, with who it is for, which plan, and whether the
 * comp agreement behind it has been signed. COM-8.91.
 *
 * Supersedes `fetchUnsignedAssignments()`, which returned ids and no names, so
 * the health card could report a count and not the people in it.
 *
 * ⚠️ THIS RETURNS ZERO ROWS TO AN UNAUTHORISED CALLER WITHOUT REFUSING, exactly
 * like the readers above, because it carries the same per-row
 * `commission_has_authority('commission.view', ...)` gate. An empty array is NOT
 * an all-clear until the authority has been asked separately.
 *
 * ⚠️ AND ZERO IS ALSO THE HONEST ANSWER WHEN THE CALLER IS LOOKING AT A
 * DIFFERENT ACCOUNT. A superadmin's scope comes from
 * `superadmin_tenant_sessions.active_tenant_id`, not `users.tenant_id`. Measured
 * 2026-08-28: the account that owns every commission line in the sandbox
 * returned nothing here because its active tenant had been switched elsewhere.
 * Nothing was wrong. The card must not read that as a regression.
 */
export async function fetchAssignmentAgreements(): Promise<AssignmentAgreement[]> {
  const { data, error } = await supabase.rpc('commission_assignment_agreements');
  fail('Could not read comp agreements', error);
  return toAgreements((data as AssignmentAgreementRow[] | null) ?? []);
}

/** How many active lines are held right now, whatever the limit says. */
export async function countHeldLines(tenantId: string): Promise<number> {
  const { count, error } = await supabase
    .from('commission_lines')
    .select('id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('payability_state', 'held')
    .eq('validity', 'active');
  fail('Could not count held lines', error);
  return count ?? 0;
}

/**
 * Declare how long is too long for a held line.
 *
 * ⚠️ THIS IS THE SWITCH, NOT A PREFERENCE. Until it is set,
 * `commission_stale_payability_holds()` returns nothing however long money sits
 * waiting, and nothing in the product could set it before COM-8.9 — the column
 * appeared in zero frontend files.
 */
export async function declareHeldLineAlertDays(days: number): Promise<void> {
  await updateTenantSettings({
    commission_payability_alert_days: days,
  } as Record<string, unknown>);
  clearTenantSettingsCache();
}

/**
 * State the share a comp agreement records for a standing assignment.
 *
 * ⚠️ COM-R.167. `commission_plan_assignments.split_percent` is required by
 * `comp_agreement_required_tokens()`, its absence is what puts an assignment on
 * this card with `comp_split_percent` unresolved, and until this function existed
 * NOTHING in the product could write it: exactly one database function touched
 * the table and it did not name the column, and no frontend or backend file
 * wrote it directly. Shipped copy told an operator to settle terms they had no
 * control to settle. Eighth instance of that shape in this epic.
 *
 * ⚠️ DOCUMENTARY. `comp_agreement_figures()` is the only reader of the column in
 * the whole schema and nothing computes money from it; what a project pays comes
 * from `project_contributors.split_percent` per deal. Stating it changes what the
 * agreement SAYS, never what anybody is paid, and the control says so.
 */
export async function stateAssignmentSplit(
  assignmentId: string,
  splitPercent: number,
): Promise<void> {
  const { error } = await supabase.rpc('commission_state_assignment_split', {
    p_assignment_id: assignmentId,
    p_split_percent: splitPercent,
  });
  fail('Could not state that split', error);
}

/**
 * COM-9.13 — the two setup steps that fail by producing nothing.
 *
 * ⚠️ UNLIKE THE THREE READERS ABOVE, THIS ONE REFUSES AN UNAUTHORISED CALLER
 * RATHER THAN ANSWERING ZERO ROWS. That is deliberate and it is the point: an
 * empty answer from a setup reader reads exactly like a configured account, which
 * is the failure the whole story is about. So an error here must surface, and the
 * caller must not render an all-clear from a catch.
 */
export async function fetchAccountReadiness(): Promise<AccountReadinessCheck[]> {
  const { data, error } = await supabase.rpc('commission_account_readiness');
  fail('Could not read account setup', error);
  return ((data as Record<string, unknown>[] | null) ?? []).map((r) => ({
    checkKey: r.check_key as AccountReadinessCheck['checkKey'],
    state: r.state as AccountReadinessCheck['state'],
    affected: (r.affected as number) ?? 0,
  }));
}
